<template>
  <div class="roles-main">
    <el-card shadow="never" class="filter-card">
      <div class="search">
        <el-date-picker
          v-model="recordTime"
          type="date"
          placeholder="Record Date"
          format="YYYY/MM/DD"
          value-format="YYYY-MM-DD"
          size="small"
        />
        <el-button
          icon="el-icon-search"
          size="small"
          type="success"
          @click="showUserOnlineStatistics()"
        >
          Search
        </el-button>
      </div>
    </el-card>
    <el-card shadow="never" class="section-card">
      <div class="card-head">
        <span class="card-title">User Online Statistics</span>
        <span class="card-badge">{{ list.records.length }} users</span>
      </div>
      <el-table
        v-loading="loading"
        :data="list.records"
        ref="table"
        row-key="userId"
        @expand-change="exChange"
        size="small"
        border
        class="beauty-table"
        highlight-current-row
        empty-text="No data"
        :cell-class-name="cellGrayWhite"
      >
        <el-table-column type="expand">
          <template #default="scope">
            <div v-if="scope.row.timelines" class="act-pane">
              <div class="act-caption">Daily status timeline</div>
              <div class="act-summary">
                <span class="act-lg">
                  <i class="act-dot" :style="{background: onlineColor}" />Online
                  <b class="num-cell">{{ formatSeconds(scope.row.onlineSeconds) }}</b>
                </span>
                <span class="act-lg">
                  <i class="act-dot" :style="{background: awayColor}" />Away
                  <b class="num-cell">{{ formatSeconds(scope.row.awaySeconds) }}</b>
                </span>
                <span class="act-lg">
                  <i class="act-dot" :style="{background: offlineColor}" />Offline
                  <b class="num-cell">{{ formatSeconds(scope.row.offlineSeconds) }}</b>
                </span>
                <span class="act-count">{{ scope.row.timelines.length }} changes</span>
              </div>
              <div class="act-bar">
                <template v-if="segView(scope.row).total > 0">
                  <el-tooltip
                    v-for="(seg, i) in segView(scope.row).segs"
                    :key="i"
                    effect="dark"
                    placement="top"
                  >
                    <template #content>
                      <div style="line-height: 1.8; font-size: 12px; padding: 2px 0;">
                        <div style="font-weight: 600; margin-bottom: 2px;">{{ seg.status }}</div>
                        <div><span style="display: inline-block; width: 70px; opacity: 0.65;">From</span>{{ seg.startLabel }}</div>
                        <div><span style="display: inline-block; width: 70px; opacity: 0.65;">To</span>{{ seg.endLabel }}</div>
                        <div><span style="display: inline-block; width: 70px; opacity: 0.65;">Duration</span>{{ formatSeconds(seg.seconds) }}</div>
                      </div>
                    </template>
                    <div
                      class="act-seg"
                      :class="{'act-seg-hl': isSegHover(scope.row, i)}"
                      :style="{width: ((seg.seconds / segView(scope.row).total) * 100).toFixed(4) + '%', background: seg.color}"
                      @mouseenter="setHoverSeg(scope.row, i)"
                      @mouseleave="clearHoverSeg()"
                    />
                  </el-tooltip>
                </template>
                <div v-else class="act-empty">No activity</div>
              </div>
              <div class="act-ends">
                <span>{{ segView(scope.row).startLabel }}</span>
                <span>{{ segView(scope.row).endLabel }}</span>
              </div>
              <div v-if="segView(scope.row).total > 0" class="act-list">
                <div
                  v-for="(seg, i) in segView(scope.row).segs"
                  :key="i"
                  class="act-list-item"
                  :class="{'act-list-hl': isSegHover(scope.row, i)}"
                  :style="{background: segBgColor(seg.status), borderLeftColor: seg.color}"
                  @mouseenter="setHoverSeg(scope.row, i)"
                  @mouseleave="clearHoverSeg()"
                >
                  <span class="act-list-no">{{ i + 1 }}</span>
                  <span class="act-list-status">{{ seg.status }}</span>
                  <span class="act-list-range num-cell">{{ seg.startLabel }} - {{ seg.endLabel }}</span>
                  <span class="act-list-dur num-cell">{{ formatSeconds(seg.seconds) }}</span>
                </div>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column
          prop="nickName"
          label="Nick name"
          align="left"
          min-width="160"
          show-overflow-tooltip
        />
        <el-table-column label="Online" header-align="right" align="right">
          <template #default="scope">
            <span class="num-cell">{{ formatSeconds(scope.row.onlineSeconds) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="Away" header-align="right" align="right">
          <template #default="scope">
            <span class="num-cell">{{ formatSeconds(scope.row.awaySeconds) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="Offline" header-align="right" align="right">
          <template #default="scope">
            <span class="num-cell">{{ formatSeconds(scope.row.offlineSeconds) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="Chart" width="400px">
          <template #default="scope">
            <div class="chart-div">
              <Chart
                :options="processPieChart(scope.row)"
                :name="scope.row.nickName"
              />
            </div>
          </template>
        </el-table-column>
      </el-table>
    </el-card>
  </div>
</template>

<script setup>
import { onMounted, reactive, ref } from '@vue/runtime-core'
import { ElMessage } from 'element-plus'
import { getUserOnlineStatistics, getUserOnlineTimeline } from '../../../api/report'
import { convertDate } from '@/utils/util'
import Chart from '@/components/charts/Charts'
import { cellGrayWhite } from '@/utils/style'
import moment from 'moment'

const offlineColor = '#8B8D91';
const onlineColor = '#77D673';
const awayColor = '#F5BD40';

const list = reactive({
  records: [],
  timelines: [],
})

const recordTime = ref(convertDate(new Date()))

const loading = ref(false)

function processPieChart(scopeData) {
  const pieData = []

  pieData.push({ value: scopeData.onlineSeconds, name: 'Online' })
  pieData.push({ value: scopeData.offlineSeconds, name: 'Offline' })
  pieData.push({ value: scopeData.awaySeconds, name: 'Away' })
  return {
    tooltip: {
      show: false,
    },
    legend: {
      orient: 'vertical',
      right: 5,
      itemWidth: 7,
      itemHeight: 7,
      fontSize: 8,
    },
    color: [onlineColor, offlineColor, awayColor],
    series: [
      {
        type: 'pie',
        radius: ['5%', '100%'],
        center: ['20%', '50%'],
        avoidLabelOverlap: false,
        itemStyle: {
          borderRadius: 1,
          borderColor: '#fff',
          borderWidth: 1,
        },
        label: {
          show: false,
        },
        emphasis: {
          label: {
            show: false,
          },
        },
        labelLine: {
          show: false,
        },
        data: pieData,
      },
    ],
  }
}

function formatSeconds(v) {
  if (v === null || v === undefined || v === '' || Number.isNaN(Number(v))) return '—'
  const n = Number(v)
  if (n === 0) return '0s'
  if (n < 60) {
    const s = Math.round(n * 10) / 10
    return (Number.isInteger(s) ? s : s.toFixed(1)) + 's'
  }
  const total = Math.round(n)
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  let out = ''
  if (h > 0) out += h + 'h'
  if (m > 0) out += m + 'm'
  if (s > 0) out += s + 's'
  return out || '0s'
}

function getTimelineIconColor(timeline) {
  if (timeline.onlineStatus === 'OFFLINE') {
    return offlineColor;
  } else if (timeline.onlineStatus === 'AWAY') {
    return awayColor;
  } else {
    return onlineColor;
  }
}

// 兼容 "YYYY-MM-DD HH:mm" 与带秒两种格式
function parseTl(s) {
  return moment(s, ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD HH:mm'])
}

// 该 timelines 对应日期的结束边界(基于 timeline 自身日期, 不依赖 recordTime, 保证缓存安全):
// 是今天 → min(now, 次日0点)(止于当前, 不把未来算进末段); 过去日期 → 次日 00:00。
function dayEndMoment(tl) {
  const dayStart = parseTl(tl[0].time).clone().startOf('day')
  const dayEnd = dayStart.clone().add(1, 'day')
  const isToday = moment().format('YYYY-MM-DD') === dayStart.format('YYYY-MM-DD')
  return isToday ? moment.min(moment(), dayEnd) : dayEnd
}

// 复用现成三色映射
function segColor(status) {
  return getTimelineIconColor({ onlineStatus: status })
}

// 状态文案大小写归一, 与图例一致
function statusLabel(status) {
  if (status === 'ONLINE') return 'Online'
  if (status === 'AWAY') return 'Away'
  if (status === 'OFFLINE') return 'Offline'
  return status || '—'
}

// 状态对应浅色底(列表项整块上色 + 左侧色条, 一眼可辨)
function segBgColor(status) {
  if (status === 'Online') return '#f0f9eb'
  if (status === 'Away') return '#fdf6ec'
  if (status === 'Offline') return '#f4f4f5'
  return '#f4f4f5'
}

// 活动条段 <-> 明细项 联动高亮(记录当前 hover 的 row + 段序号)
const hoverSeg = reactive({ row: null, index: -1 })
function setHoverSeg(row, index) {
  hoverSeg.row = row
  hoverSeg.index = index
}
function clearHoverSeg() {
  hoverSeg.row = null
  hoverSeg.index = -1
}
function isSegHover(row, index) {
  return hoverSeg.row === row && hoverSeg.index === index
}

// 单次计算入口 + WeakMap 缓存(结果只依赖 row.timelines, 模板多次调用只算一次)
const _segCache = new WeakMap()
function segView(row) {
  const empty = { segs: [], total: 0, startLabel: '—', endLabel: '—' }
  if (!row) return empty
  if (_segCache.has(row)) return _segCache.get(row)
  const tl = row.timelines
  let view = empty
  if (tl && tl.length) {
    const dayEnd = dayEndMoment(tl)
    const nextDay = parseTl(tl[0].time).clone().startOf('day').add(1, 'day')
    const segs = []
    for (let i = 0; i < tl.length; i++) {
      const start = parseTl(tl[i].time)
      const next = i < tl.length - 1 ? parseTl(tl[i + 1].time) : dayEnd
      if (!start.isValid() || !next.isValid()) continue
      const seconds = next.diff(start, 'seconds')
      if (!(seconds > 0)) continue
      const isLast = i === tl.length - 1
      segs.push({
        status: statusLabel(tl[i].onlineStatus),
        color: segColor(tl[i].onlineStatus),
        seconds,
        startLabel: start.format('HH:mm'),
        endLabel: isLast && next.isSame(nextDay) ? '24:00' : next.format('HH:mm'),
      })
    }
    const total = segs.reduce((a, s) => a + s.seconds, 0)
    const firstStart = parseTl(tl[0].time)
    view = {
      segs,
      total,
      startLabel: firstStart.isValid() ? firstStart.format('HH:mm') : '—',
      endLabel: dayEnd.isSame(nextDay) ? '24:00' : dayEnd.format('HH:mm'),
    }
  }
  _segCache.set(row, view)
  return view
}

async function showUserOnlineStatistics() {
  loading.value = true
  try {
    const { data: res } = await getUserOnlineStatistics(recordTime.value)
    list.records = res
  } catch (e) {
    ElMessage.error('Failed to load data')
  } finally {
    loading.value = false
  }
}

async function exChange(row) {
  if (!row.timelines) {
    try {
      const { data: res } = await getUserOnlineTimeline(recordTime.value, row.userId)
      if (res.length) {
        row.timelines = res;
      }
    } catch (e) {
      ElMessage.error('Failed to load data')
    }
  }
}

onMounted(async () => {
  await showUserOnlineStatistics()
})
</script>

<style scoped>
.chart-div {
  width: 180px;
  height: 70px;
}

.filter-card {
  margin-bottom: 16px;
  border-radius: 8px;
}

.filter-card :deep(.el-card__body) {
  padding: 16px 18px;
}

.search {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  row-gap: 10px;
}

.search > *:not(h6) + * {
  margin-left: 20px;
}

.section-card {
  border-radius: 10px;
  margin-bottom: 16px;
}

.section-card :deep(.el-card__body) {
  padding: 16px 18px;
}

.card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}

.card-title {
  font-size: 15px;
  font-weight: 600;
  color: var(--el-text-color-primary, #303133);
}

.card-badge {
  font-size: 12px;
  color: var(--el-text-color-secondary, #909399);
  font-variant-numeric: tabular-nums;
}

.beauty-table :deep(thead th) {
  background: #f5f7fa;
  font-weight: 600;
  color: var(--el-text-color-primary, #303133);
}

.num-cell {
  font-variant-numeric: tabular-nums;
}

/* 展开行: 固定高度活动时间条 */
.act-pane {
  padding: 10px 14px 8px;
}
.act-caption {
  font-size: 11px;
  color: var(--el-text-color-secondary, #909399);
  margin-bottom: 8px;
}
.act-summary {
  display: flex;
  align-items: center;
  gap: 18px;
  font-size: 12px;
  color: var(--el-text-color-secondary, #909399);
  margin-bottom: 8px;
}
.act-lg {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}
.act-lg b {
  color: var(--el-text-color-primary, #303133);
  font-weight: 600;
}
.act-dot {
  width: 9px;
  height: 9px;
  border-radius: 2px;
  display: inline-block;
}
.act-count {
  margin-left: auto;
  font-variant-numeric: tabular-nums;
}
.act-bar {
  display: flex;
  width: 100%;
  height: 28px;
  border-radius: 6px;
  overflow: hidden;
  background: #f0f2f5;
}
.act-seg {
  height: 100%;
  min-width: 2px;
  cursor: pointer;
  transition: filter 0.15s;
}
.act-seg:hover {
  filter: brightness(1.08);
}
.act-seg-hl {
  filter: brightness(1.15);
  box-shadow: inset 0 0 0 2px rgba(0, 0, 0, 0.35);
}
.act-empty {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: var(--el-text-color-secondary, #909399);
}
.act-ends {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: var(--el-text-color-secondary, #909399);
  font-variant-numeric: tabular-nums;
  margin-top: 6px;
}
.act-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 6px 14px;
  margin-top: 14px;
  max-height: 240px;
  overflow-y: auto;
  padding-right: 4px;
}
.act-list-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: var(--el-text-color-regular, #606266);
  padding: 5px 10px;
  border-left: 3px solid #dcdfe6;
  border-radius: 0 4px 4px 0;
}
.act-list-status {
  width: 56px;
  font-weight: 600;
  color: var(--el-text-color-primary, #303133);
}
.act-list-range {
  color: var(--el-text-color-secondary, #909399);
}
.act-list-dur {
  margin-left: auto;
  font-weight: 600;
  color: var(--el-text-color-primary, #303133);
}
.act-list-no {
  flex: 0 0 18px;
  height: 18px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.06);
  color: var(--el-text-color-secondary, #909399);
  font-size: 11px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-variant-numeric: tabular-nums;
}
.act-list-hl {
  box-shadow: 0 0 0 2px var(--el-color-primary, #409eff);
}
</style>
